'use client';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import { Box, Button, Flex, Tooltip } from '@chakra-ui/react';
import cn from 'classnames';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import React, { useEffect, useRef } from 'react';

import Styles from './BuildAndBuyConfigurator.module.scss';

export interface MenuItem {
  label: string;
  path: string;
}

interface BuildPriceHeaderProps {
  menuItems: MenuItem[];
  tooltipMessages?: string[];
  basePath?: string;
}

export const BuildAndBuyConfiguratorHeader = ({
  menuItems,
  tooltipMessages = [],
  basePath = '/build-price',
}: BuildPriceHeaderProps) => {
  const navRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    const activeLink = document.querySelector(
      `.${Styles.menuLinkActive}`,
    ) as HTMLAnchorElement;
    const nav = navRef.current;

    if (activeLink && nav) {
      const linkRect = activeLink.getBoundingClientRect();
      const navRect = nav.getBoundingClientRect();

      const scrollAmount =
        linkRect.left - navRect.left + linkRect.width / 2 - navRect.width / 2;

      nav.scrollLeft += scrollAmount;
    }
  }, [pathname]);

  const getActiveMenuIndex = () => {
    const pathParts = pathname.split('/').filter(Boolean);

    for (let i = menuItems.length - 1; i >= 0; i--) {
      const itemParts = menuItems[i]?.path.split('/').filter(Boolean);

      if (itemParts && itemParts?.length <= pathParts.length) {
        const match = itemParts?.every(
          (part, index) => part.startsWith(':') || part === pathParts[index],
        );

        if (match) return i;
      }
    }

    return -1;
  };

  const generateHref = (itemPath: string) => {
    const pathParts = pathname.split('/').filter(Boolean);
    const itemParts = itemPath.split('/').filter(Boolean);

    return (
      '/' +
      itemParts
        .map((part, index) => {
          if (part.startsWith(':')) {
            return pathParts[index] || part;
          }
          return part;
        })
        .join('/')
    );
  };

  const isLinkDisabled = (index: number) => {
    const pathParts = pathname.split('/').filter(Boolean);

    return index > pathParts.length - 1;
  };

  const getTooltipMessage = (index: number) => {
    return tooltipMessages[index - 1] || '';
  };

  const handleBackClick = () => {
    const currentIndex = getActiveMenuIndex();

    if (currentIndex > 0) {
      const previousPath = generateHref(menuItems[currentIndex - 1]!.path);

      router.push(previousPath);
    } else {
      router.push(basePath);
    }
  };

  const activeIndex = getActiveMenuIndex();

  return (
    <nav
      className={cn(
        Styles.buildPriceNavBar,
        pathname === basePath && Styles.darkNavBar,
      )}
    >
      <Box className={Styles.mainNav}>
        <Flex className={Styles.flexWrapper} alignItems={'center'}>
          {activeIndex > 0 ? (
            <Button
              onClick={handleBackClick}
              className={Styles.backBtn}
              padding={0}
              role="button"
            >
              <ChevronLeftIcon role="button" />
              <Box className={Styles.backBtnText} role="button">
                Back
              </Box>
            </Button>
          ) : (
            <Box />
          )}
          <Box className={Styles.KiaOneNavigation} ref={navRef}>
            <ul>
              {menuItems.map((item, index) => (
                <li key={index} className={Styles.menuItem}>
                  <Tooltip
                    label={getTooltipMessage(index)}
                    isDisabled={!isLinkDisabled(index)}
                  >
                    <Box>
                      {isLinkDisabled(index) ? (
                        <span
                          className={cn(
                            Styles.menuLink,
                            Styles.menuLinkDisabled,
                          )}
                        >
                          {item.label}
                        </span>
                      ) : (
                        <Link
                          className={cn(
                            Styles.menuLink,
                            index === activeIndex && Styles.menuLinkActive,
                          )}
                          href={generateHref(item.path)}
                          passHref
                        >
                          {item.label}
                        </Link>
                      )}
                    </Box>
                  </Tooltip>
                </li>
              ))}
            </ul>
          </Box>
          <Box w={{ base: '0', md: '55px' }} />
        </Flex>
      </Box>
    </nav>
  );
};
